import {
  Component,
  AfterViewInit,
  OnDestroy,
  ContentChild,
  ElementRef,
  Input,
  Renderer2,
  HostListener,
  ChangeDetectorRef
} from '@angular/core';

export type DropDownOrientation =
  | 'left' | 'right' | 'top' | 'bottom'
  | 'left-top' | 'left-bottom' | 'right-top' | 'right-bottom';

@Component({
  selector: 'app-drop-down-menu',
  templateUrl: './drop-down-menu.component.html',
  styleUrls: ['./drop-down-menu.component.css']
})
export class DropDownMenuComponent implements AfterViewInit, OnDestroy {
  @ContentChild('trigger', { read: ElementRef, static: false }) trigger!: ElementRef<HTMLElement>;
  @ContentChild('content', { read: ElementRef, static: false }) content!: ElementRef<HTMLElement>;

  @Input() space = 12;
  @Input() orientation: DropDownOrientation = 'left';

  isActive = false;
  coordinations = { x: 0, y: 0 };

  private unlistenTrigger?: () => void;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: MouseEvent): void {
    if (this.isActive && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isActive) this.close();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isActive) this.updatePosition();
  }

  ngAfterViewInit(): void {
    if (this.trigger) {
      this.unlistenTrigger = this.renderer.listen(
        this.trigger.nativeElement,
        'click',
        () => this.toggleMenu()
      );
    }
  }

  ngOnDestroy(): void {
    this.unlistenTrigger?.();
  }

  toggleMenu(): void {
    this.isActive ? this.close() : this.open();
  }

  private open(): void {
    this.isActive = true;
    // Le *ngIf vient de rendre #content : on attend le prochain tick
    // pour que le ContentChild soit disponible et mesurable.
    Promise.resolve().then(() => {
      this.cdr.detectChanges();
      this.updatePosition();
    });
  }

  private close(): void {
    this.isActive = false;
    this.coordinations = { x: 0, y: 0 };
  }

  private updatePosition(): void {
    this.coordinations = this.getPosition();
  }

  private getPosition(): { x: number; y: number } {
    if (!this.trigger || !this.content) return { x: 0, y: 0 };

    const triggerRect = this.trigger.nativeElement.getBoundingClientRect();
    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const contentEl = this.content.nativeElement;
    const contentWidth = contentEl.offsetWidth;
    const contentHeight = contentEl.offsetHeight;

    // Coordonnées relatives au conteneur .drop-down (position: relative)
    const triggerX = triggerRect.left - hostRect.left;
    const triggerY = triggerRect.top - hostRect.top;
    const triggerW = triggerRect.width;
    const triggerH = triggerRect.height;

    switch (this.orientation) {
      case 'left':
        return { x: triggerX - contentWidth - this.space, y: triggerY };
      case 'right':
        return { x: triggerX + triggerW + this.space, y: triggerY };
      case 'top':
        return { x: triggerX, y: triggerY - contentHeight - this.space };
      case 'bottom':
        return { x: triggerX, y: triggerY + triggerH + this.space };
      case 'left-top':
        return { x: triggerX - contentWidth - this.space, y: triggerY - contentHeight + triggerH };
      case 'left-bottom':
        return { x: triggerX - contentWidth - this.space, y: triggerY };
      case 'right-top':
        return { x: triggerX + triggerW + this.space, y: triggerY - contentHeight + triggerH };
      case 'right-bottom':
        return { x: triggerX + triggerW + this.space, y: triggerY };
      default:
        return { x: 0, y: 0 };
    }
  }
}